import React, { useState, useRef, useEffect } from 'react';
import { lbl, inp, row2, sectionDivider, sectionHeading, DateInput } from './shared';
import { searchPharmaciesByName } from '../../services/npiService';

// Note: YesNo, MultiSelect, and income data moved to BenefitsIncome.js

const MPF_URL = 'https://mpf.translatinginsurance.com';

const dropdownStyle = {
  position: 'absolute', top: '100%', left: 0, right: 0,
  background: '#fff', border: '1.5px solid #c5d5e8', borderRadius: 8,
  zIndex: 200, maxHeight: 220, overflowY: 'auto',
  boxShadow: '0 6px 16px rgba(26,51,100,0.13)', marginTop: 2,
};

const optionStyle = () => ({
  padding: '9px 14px', cursor: 'pointer', fontSize: 14, color: '#1a3364', background: '#fff',
});


const PROVIDER_OPTIONS = [
  'Aetna', 'Anthem', 'Blue Cross Blue Shield', 'Bright Health',
  'CareFirst BlueCross BlueShield', 'Centene', 'Cigna',
  'CVS Health / Caremark', 'Florida Blue', 'Harvard Pilgrim Health Care',
  'Health Net', 'HealthPartners', 'Highmark', 'Humana',
  'Independence Blue Cross', 'Kaiser Permanente', 'Medica',
  'Molina Healthcare', 'Oscar Health', 'Premera Blue Cross',
  'Priority Health', 'Providence Health Plan',
  'Regence BlueCross BlueShield', 'Tufts Health Plan',
  'UPMC Health Plan', 'UnitedHealthcare', 'WellCare',
].sort();

// ─── PharmacySearch ───────────────────────────────────────────────────────────

function PharmacySearch({ zip, state, onAdd }) {
  const [query, setQuery] = useState('');
  const [catalog, setCatalog] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const loadedRef = useRef(false);
  const searchTimerRef = useRef(null);

  useEffect(() => {
    loadedRef.current = false;
    setCatalog([]);
  }, [zip, state]);

  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function loadCatalog() {
    if (loadedRef.current || (!zip && !state)) return;
    const params = new URLSearchParams();
    if (zip)   params.set('zip_code', zip);
    if (state) params.set('state', state);
    params.set('radius', '40');
    setLoading(true);
    fetch(`${MPF_URL}/pharmacies/search?${params}`)
      .then(r => r.json())
      .then(data => { setCatalog(Array.isArray(data) ? data : []); loadedRef.current = true; })
      .catch(() => { loadedRef.current = true; })
      .finally(() => setLoading(false));
  }

  function handleQueryChange(e) {
    const val = e.target.value;
    setQuery(val);
    setOpen(true);

    clearTimeout(searchTimerRef.current);
    const q = val.trim();
    if (q.length >= 2) {
      setSearching(true);
      searchTimerRef.current = setTimeout(async () => {
        try {
          const results = await searchPharmaciesByName(q, zip, state);
          setSearchResults(results);
        } catch {
          setSearchResults([]);
        }
        setSearching(false);
      }, 350);
    } else {
      setSearchResults([]);
      setSearching(false);
    }
  }

  function select(pharm) {
    onAdd({ Pharmacy_Name: pharm.name, Pharmacy_NPI: pharm.npi || null, Pharmacy_Address: pharm.address || '', Pharmacy_City: pharm.city || '', Pharmacy_State: pharm.state || '' });
    setQuery('');
    setSearchResults([]);
    setOpen(false);
  }

  function addManual() {
    const name = query.trim();
    if (!name) return;
    onAdd({ Pharmacy_Name: name, Pharmacy_NPI: null, Pharmacy_Address: '', Pharmacy_City: '', Pharmacy_State: '' });
    setQuery('');
    setSearchResults([]);
    setOpen(false);
  }

  const q = query.trim();
  const isLiveSearch = q.length >= 2;
  const isLoading = loading || (isLiveSearch && searching);
  const visible = isLiveSearch ? searchResults : catalog.slice(0, 30);
  const showCustom = isLiveSearch && visible.length === 0 && !isLoading;

  return (
    <div ref={containerRef} style={{ position: 'relative', marginBottom: 10 }}>
      <input
        style={{ ...inp, background: '#fff' }}
        value={query}
        placeholder={loading ? 'Loading nearby pharmacies…' : 'Search pharmacies by name…'}
        autoComplete="off"
        onChange={handleQueryChange}
        onFocus={() => { loadCatalog(); setOpen(true); }}
        onKeyDown={e => { if (e.key === 'Escape') setOpen(false); if (e.key === 'Enter' && showCustom) addManual(); }}
      />
      {open && (isLoading || visible.length > 0 || showCustom) && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1.5px solid #c5d5e8', borderRadius: 8, zIndex: 200, maxHeight: 260, overflowY: 'auto', boxShadow: '0 6px 16px rgba(26,51,100,0.13)', marginTop: 2 }}>
          {isLoading && <div style={{ padding: '10px 14px', fontSize: 13, color: '#7a99b8' }}>{isLiveSearch ? 'Searching…' : 'Loading nearby pharmacies…'}</div>}
          {!isLoading && visible.map(pharm => (
            <div
              key={pharm.npi}
              onMouseDown={() => select(pharm)}
              style={{ padding: '9px 14px', cursor: 'pointer', borderBottom: '1px solid #f4f8fc' }}
              onMouseEnter={e => e.currentTarget.style.background = '#dbeef8'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              <div style={{ fontWeight: 600, fontSize: 14, color: '#1a3364' }}>{pharm.name}</div>
              <div style={{ fontSize: 12, color: '#7a99b8', marginTop: 1 }}>{[pharm.address, pharm.city, pharm.state, pharm.zip].filter(Boolean).join(', ')}</div>
            </div>
          ))}
          {showCustom && (
            <div onMouseDown={addManual} style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 14, color: '#7a99b8', fontStyle: 'italic' }}
              onMouseEnter={e => e.currentTarget.style.background = '#dbeef8'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
              Add "{query.trim()}" manually
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── ProviderTypeahead ────────────────────────────────────────────────────────

function ProviderTypeahead({ value, onChange, placeholder }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const matches = query.length >= 1
    ? PROVIDER_OPTIONS.filter(p => p.toLowerCase().includes(query.toLowerCase()))
    : PROVIDER_OPTIONS;

  function select(name) {
    setQuery(name);
    onChange(name);
    setOpen(false);
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        style={inp}
        value={query}
        placeholder={placeholder}
        autoComplete="off"
        onChange={e => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
      />
      {open && matches.length > 0 && (
        <div style={dropdownStyle}>
          {matches.map(p => (
            <div
              key={p}
              onMouseDown={() => select(p)}
              style={optionStyle(false)}
              onMouseEnter={e => e.currentTarget.style.background = '#dbeef8'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              {p}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Insurance({ data, onChange }) {
  function updateProvider(i, val) {
    const arr = [...(data.preferredProviders || [''])];
    arr[i] = val;
    onChange('preferredProviders', arr);
  }

  return (
    <div>
      {/* Medicare fields */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
        <div>
          <label style={lbl}>Medicare Number</label>
          <input style={inp} value={data.medicareNumber} onChange={e => onChange('medicareNumber', e.target.value)} />
        </div>
        <div>
          <label style={lbl}>Part A Effective Date</label>
          <DateInput style={inp} value={data.partADate} onChange={v => onChange('partADate', v)} />
        </div>
        <div>
          <label style={lbl}>Part B Effective Date</label>
          <DateInput style={inp} value={data.partBDate} onChange={v => onChange('partBDate', v)} />
        </div>
      </div>

      <div style={row2}>
        <div>
          <label style={lbl}>Medicaid Number</label>
          <input style={inp} value={data.medicaidNumber} onChange={e => onChange('medicaidNumber', e.target.value)} />
        </div>
        <div>
          <label style={lbl}>Federal LIS</label>
          <input style={inp} value={data.federalLIS} onChange={e => onChange('federalLIS', e.target.value)} />
        </div>
      </div>


      {/* Pharmacy */}
      <div style={sectionDivider}>
        <div style={sectionHeading}>Current Pharmacy</div>
        <p style={{ fontSize: 13, color: '#4a6288', marginTop: 0, marginBottom: 14 }}>
          Search pharmacies near your ZIP. Click to add one or more.
        </p>
        <PharmacySearch
          zip={data.zip}
          state={data.state}
          onAdd={pharm => onChange('pharmacies', [...(data.pharmacies || []), pharm])}
        />
        {(data.pharmacies || []).filter(p => p && (p.name || p)).map((p, i) => {
          const name    = typeof p === 'object' ? (p.Pharmacy_Name || p.name || '') : p;
          const address = typeof p === 'object' ? [p.Pharmacy_Address || p.address, p.Pharmacy_City || p.city, p.Pharmacy_State || p.state].filter(Boolean).join(', ') : '';
          const npi     = typeof p === 'object' ? (p.Pharmacy_NPI || p.npi || null) : null;
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', border: '1.5px solid #dbeef8', borderRadius: 8, padding: '10px 14px', marginBottom: 8 }}>
              <div>
                <div style={{ fontWeight: 700, color: '#1a3364', fontSize: 14 }}>{name}</div>
                {address && <div style={{ fontSize: 12, color: '#7a99b8', marginTop: 2 }}>{address}</div>}
                {npi && <div style={{ fontSize: 11, color: '#9ab0c8', marginTop: 1 }}>NPI: {npi}</div>}
              </div>
              <button type="button" onClick={() => onChange('pharmacies', (data.pharmacies || []).filter((_, idx) => idx !== i))} style={{ padding: '5px 10px', border: 'none', background: '#f5e8e8', borderRadius: 6, cursor: 'pointer', color: '#b03030', fontWeight: 700, fontSize: 14, flexShrink: 0, marginLeft: 12 }}>
                ×
              </button>
            </div>
          );
        })}
      </div>


      {/* Preferred Providers */}
      <div style={sectionDivider}>
        <div style={sectionHeading}>Preferred Providers</div>
        <p style={{ fontSize: 13, color: '#4a6288', marginTop: 0, marginBottom: 14 }}>
          Insurance companies or health networks you prefer (e.g. UnitedHealthcare, Kaiser, Providence).
        </p>
        {(data.preferredProviders || ['']).map((val, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <ProviderTypeahead
                value={val}
                onChange={v => updateProvider(i, v)}
                placeholder="Type to search or enter any name…"
              />
            </div>
            {(data.preferredProviders || ['']).length > 1 && (
              <button
                type="button"
                onClick={() => onChange('preferredProviders', (data.preferredProviders || ['']).filter((_, idx) => idx !== i))}
                style={{ padding: '9px 12px', border: 'none', background: '#f5e8e8', borderRadius: 8, cursor: 'pointer', color: '#b03030', fontWeight: 700, fontSize: 18, lineHeight: 1, flexShrink: 0 }}
              >
                ×
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={() => onChange('preferredProviders', [...(data.preferredProviders || ['']), ''])}
          style={{ padding: '9px 20px', border: '2px dashed #2a5298', borderRadius: 8, background: '#f4f8fc', color: '#2a5298', fontWeight: 600, fontSize: 14, cursor: 'pointer', marginTop: 2 }}
        >
          + Add Provider
        </button>
      </div>
    </div>
  );
}
